/**
 * Secure Access → App Connectors
 *
 * App Connector inventory, health and group membership. Connectors dial out to
 * a gateway; nothing here opens an inbound port on them.
 *
 * Active / Pending Approval tabs mirror the Edges page (VSNP-1096); row actions
 * (approve / decommission) reuse the shared icons.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, RefreshCw, CheckCircle2, Trash2, Boxes, HeartPulse, ChevronRight } from 'lucide-react';
import DataTable, { type Column } from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import RegisterNodeWizard from '../../components/access/RegisterNodeWizard';
import ClaimsPanel from '../../components/access/ClaimsPanel';
import ConnectorGroupModal from '../../components/access/ConnectorGroupModal';
import { nodeStatusLabel } from '../../components/access/nodeStatus';
import { useToast } from '../../components/common/ToastContainer';
import ZtnaChip from '../../components/access/ZtnaChip';
import accessService, {
  type AppConnector,
  type ConnectorGroupSummary,
} from '../../services/access.service';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';

const AccessConnectors = () => {
  const toast = useToast();

  const [connectors, setConnectors] = useState<AppConnector[]>([]);
  const [groups, setGroups] = useState<ConnectorGroupSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'active' | 'pending'>('active');
  const [decommissionTarget, setDecommissionTarget] = useState<AppConnector | null>(null);
  const [decommissioning, setDecommissioning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped whenever the wizard mints a token so the claims panel refetches.
  const [claimsRefresh, setClaimsRefresh] = useState(0);

  // New-group quick-create modal (VSNP-1097). Full group management — description,
  // membership, delete — lives on the group detail page (/access/connectors/groups/:name).
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, groupList] = await Promise.all([
        accessService.listConnectors({ limit: 200 }),
        accessService.listConnectorGroups(),
      ]);
      setConnectors(list?.items ?? []);
      setGroups(groupList ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load connectors');
      setConnectors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const handleApprove = async (connector: AppConnector) => {
    try {
      await accessService.approveConnector(connector.id);
      toast.success('Connector approved', connector.name ?? connector.node_id);
      await fetchAll();
    } catch (err) {
      toast.error('Approve failed', err instanceof Error ? err.message : 'Could not approve the connector');
    }
  };

  const handleDecommission = async () => {
    if (!decommissionTarget) return;
    try {
      setDecommissioning(true);
      await accessService.decommissionConnector(decommissionTarget.id);
      toast.success('Connector decommissioned', decommissionTarget.name ?? decommissionTarget.node_id);
      setDecommissionTarget(null);
      await fetchAll();
    } catch (err) {
      toast.error('Decommission failed', err instanceof Error ? err.message : 'Could not decommission the connector');
    } finally {
      setDecommissioning(false);
    }
  };

  const dateFormat = useSettingsStore.getState().getDateFormatSettings();

  const columns: Column<AppConnector>[] = [
    {
      key: 'name',
      label: 'Connector',
      sortable: true,
      render: (c) => (
        <Link to={`/access/connectors/${c.id}`} className="block">
          <div className="font-medium text-gray-900 hover:text-primary-600">{c.name ?? '—'}</div>
          <div className="text-xs text-gray-500 font-mono">{c.node_id.slice(0, 18)}…</div>
        </Link>
      ),
    },
    { key: 'connector_group', label: 'Group', sortable: true },
    { key: 'site', label: 'Site', render: (c) => c.site ?? '—' },
    { key: 'version', label: 'Version', render: (c) => c.version ?? '—' },
    { key: 'health', label: 'Health', sortable: true, render: (c) => <StatusBadge status={c.health} /> },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (c) => <StatusBadge status={nodeStatusLabel(c.status, c.certificate_serial_number)} />,
    },
    {
      key: 'last_seen',
      label: 'Last Seen',
      sortable: true,
      render: (c) => (c.last_seen ? formatDate(c.last_seen, dateFormat) : 'never'),
    },
    {
      key: 'actions',
      label: '',
      render: (c) => (
        <div className="flex items-center justify-end gap-1">
          {c.status === 'pending_approval' && c.certificate_serial_number && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-success-50 text-success-600"
              title="Approve connector"
              onClick={() => void handleApprove(c)}
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          )}
          {c.status !== 'decommissioned' && (
            <button
              type="button"
              className="p-1.5 rounded hover:bg-danger-50 text-gray-400 hover:text-danger-600"
              title="Decommission connector"
              onClick={() => setDecommissionTarget(c)}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const groupColumns: Column<ConnectorGroupSummary>[] = [
    {
      key: 'connectorGroup',
      label: 'Group',
      sortable: true,
      render: (g) => (
        <Link
          to={`/access/connectors/groups/${encodeURIComponent(g.connectorGroup)}`}
          className="flex items-center gap-1.5 font-medium text-primary-600 hover:underline"
        >
          {g.connectorGroup}
          {!g.registered && (
            <span
              className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-normal text-gray-600"
              title="Referenced by connectors/apps but not saved as a group. Open it to define and manage it."
            >
              not defined
            </span>
          )}
        </Link>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      render: (g) =>
        g.description ? (
          <span className="text-sm text-gray-600">{g.description}</span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
    },
    {
      key: 'total',
      label: 'Connectors',
      sortable: true,
      render: (g) => (
        <span className="text-sm text-gray-600">
          {g.total === 0 ? 'none' : `${g.healthy}/${g.total} healthy`}
        </span>
      ),
    },
    {
      key: 'manage',
      label: '',
      render: () => (
        <div className="flex justify-end">
          <ChevronRight className="h-4 w-4 text-gray-300" />
        </div>
      ),
    },
  ];

  const healthy = connectors.filter((c) => c.health === 'up').length;
  const pending = connectors.filter((c) => c.status === 'pending_approval');
  const active = connectors.filter((c) => c.status !== 'pending_approval');
  const shown = activeTab === 'pending' ? pending : active;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold text-gray-900">App Connectors</h1>
            <ZtnaChip />
          </div>
          <p className="text-gray-600 mt-1">Connectors front private apps and dial out to a gateway</p>
          <p className="text-xs text-gray-400 mt-1">
            Looking for v2 site connector edges? → Equipment → Connectors
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchAll()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-primary" onClick={() => setIsAddOpen(true)}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Create Connector
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-4 gap-4">
        <StatCard title="Connectors" value={connectors.length} icon={Boxes} color="primary" loading={loading} />
        <StatCard title="Healthy" value={healthy} icon={HeartPulse} color="success" loading={loading} />
        <StatCard
          title="Pending Approval"
          value={pending.length}
          icon={CheckCircle2}
          color={pending.length > 0 ? 'warning' : 'secondary'}
          loading={loading}
        />
        <StatCard title="Groups" value={groups.length} icon={Boxes} color="info" loading={loading} />
      </div>

      <div className="card">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Connector Groups</h2>
            <p className="text-xs text-gray-400">A connector belongs to one group; apps target a group. Open a group to manage its members.</p>
          </div>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() => setGroupModalOpen(true)}
          >
            <Plus className="mr-1 inline h-4 w-4" />
            New Group
          </button>
        </div>
        <DataTable
          columns={groupColumns}
          data={groups}
          keyExtractor={(g) => g.connectorGroup}
          loading={loading}
          searchPlaceholder="Search groups…"
          emptyMessage="No groups yet. Create one to organise connectors, then target it from an Access Application."
        />
      </div>

      {/* Tabs (Edges pattern) */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('active')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'active'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Active Connectors
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm inline-flex items-center space-x-2 ${
              activeTab === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span>Pending Approval</span>
            {pending.length > 0 && (
              <span
                className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none rounded-full ${
                  activeTab === 'pending' ? 'bg-primary-100 text-primary-700' : 'bg-orange-100 text-orange-700 animate-pulse'
                }`}
              >
                {pending.length}
              </span>
            )}
          </button>
        </nav>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={shown}
          loading={loading}
          searchPlaceholder="Search connectors…"
          emptyMessage={
            activeTab === 'pending'
              ? 'No connectors awaiting approval'
              : 'No connectors yet — Create Connector makes the record and hands you a one-time enrolment token for the VM.'
          }
        />
      </div>

      <ClaimsPanel
        deviceType="app_connector"
        nodeNames={Object.fromEntries(connectors.map((c) => [c.id, c.name ?? c.node_id]))}
        refreshKey={claimsRefresh}
      />

      <RegisterNodeWizard
        kind="app_connector"
        isOpen={isAddOpen}
        onClose={() => {
          setIsAddOpen(false);
          setClaimsRefresh((n) => n + 1);
        }}
        onRegistered={() => void fetchAll()}
        connectorGroups={groups.map((g) => g.connectorGroup)}
      />

      <ConfirmDialog
        isOpen={!!decommissionTarget}
        onClose={() => setDecommissionTarget(null)}
        onConfirm={handleDecommission}
        title="Decommission connector"
        message={`Decommission '${decommissionTarget?.name ?? decommissionTarget?.node_id}'? Flows pinned to it break and re-establish via surviving connectors in the group.`}
        confirmText="Decommission"
        variant="danger"
        isLoading={decommissioning}
      />

      <ConnectorGroupModal
        isOpen={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        onSaved={() => void fetchAll()}
        group={null}
      />
    </div>
  );
};

export default AccessConnectors;
